import { h } from 'preact';
import { joinClasses } from 'utils/utils';
// import formatDateTime from 'utils/formatDateTime';
import { getDataPathFromItem } from 'utils/data';
import { showModal } from 'utils/modal';
import { getSimplePreviewPageLink, preparePreviewData } from 'utils/preview';

import MainContent from 'components/main/content';
import Button from 'components/button/button';
import Badge from 'components/badge/badge';
import Dropdown from 'components/dropdown/dropdown';
import LoadingBar from 'components/loadingBar/loadingBar';
import VersionHistory from 'components/versionHistory/versionHistory';
import WithTooltip from 'components/tooltip/withTooltip';

import s from 'components/list/list.sss';

export default class List extends MainContent {
	s = s;

	get itemsSource() {
		return {};
	}

	get attributes() {
		return {
			name: { name: 'Name', accessor: this.getItemName, required: true, render: this.renderListItemName },
			lastModified: { name: 'Modified', type: 'datetime', accessor: this.getItemModifiedDate },
			lastPublished: { name: 'Published', type: 'datetime', accessor: this.getItemPublishedDate },
			status: { name: 'Status', accessor: this.getItemStatus, render: this.renderStatus },
		};
	}

	get sortNames() {
		return {
			default: { asc: 'ascending', desc: 'descending' },
			date: { asc: 'oldest first', desc: 'recent first' },
		};
	}

	get sortOptions() {
		return ['lastModified', 'lastPublished', 'name'];
	}

	get defaultSort() {
		return { sort: 'lastModified', sortDirection: 'desc' };
	}

	get availableFilters() {
		return {
			liveUpdated: { name: 'Live - up to date', run: item => item.commits?.publish && !(item.commits?.current?.at > item.commits?.publish?.at) },
			liveUnpublished: { name: 'Live - unpublished changes', run: item => item.commits?.publish && item.commits?.current?.at > item.commits?.publish?.at },
			draft: { name: 'Draft', run: item => !item.commits?.publish },
			// sentForApproval: { name: 'Sent for approval', run: () => false },
			// vowelStart: { name: 'Name starts with a vowel', run: item => /^[aeiou]/i.test(item.name) },
		};
	}

	constructor(props) {
		super(props);

		this.viewModes = {
			grid: { name: 'Grid', render: this.renderGrid },
			list: { name: 'List', render: this.renderList },
		};
		this.state = {
			viewMode: 'grid',
			// sort: 'lastModified',
			// sortDirection: 'desc',
			filters: [],
			search: '',
			page: '',
			dragging: false,
			listId: crypto.randomUUID(),
		};
		this.state = { ...this.state, ...this.defaultSort };

		// storing these for more efficient rendering
		this.dateFormatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
		// separate date and time to avoid "at" in between them
		this.timeFormatter = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' });

		this.clearFilters = this.clearFilters.bind(this);
		this.selectView = this.selectView.bind(this);
		this.toggleColumn = this.toggleColumn.bind(this);
		this.selectFilter = this.selectFilter.bind(this);
		this.onGridSortSelection = this.onGridSortSelection.bind(this);
		this.onListSortSelection = this.onListSortSelection.bind(this);
		this.renderGridItem = this.renderGridItem.bind(this);
		this.onSearchInput = this.onSearchInput.bind(this);
	}

	componentWillMount() {
		this.state.columns = Object.fromEntries(Object.entries(this.attributes).map(([key, attr]) => [key, !attr.defaultHidden]));
	}

	componentDidUpdate() {
		this.checkSort();
	}

	checkSort() {
		const options = this.sortOptions;
		if (!options.includes(this.state.sort)) {
			const newSort = this.defaultSort;
			// Extra check to avoid setState loop in case of misconfiguration
			if (options.includes(newSort.sort)) {
				this.setState(newSort);
			}
		}
	}

	getItemAttribute(item, key) {
		const accessor = this.attributes[key]?.accessor;
		return accessor ? accessor(item) : item[key];
	}

	chooseLaterDate(a, b) {
		if (a == null) return b;
		if (b == null) return a;
		return a > b ? a : b;
	}

	search(item) {
		if (!this.state.search) return true;
		return item.name.toLowerCase().includes(this.state.search.toLowerCase());
	}

	getFilterOptions() {
		const allItems = Object.values(this.itemsSource || {});
		return Object.entries(this.availableFilters || {}).map(([key, { name, run }]) => ({
			key,
			name,
			disabled: !allItems.some(item => run.call(this, item))
		}));
	}

	getItems(items = Object.values(this.itemsSource || {})) {
		const sort = this.state.sort;
		const sortMult = this.state.sortDirection === 'desc' ? -1 : 1;

		return items.filter(item => {
			if (this.state.filters.length && !this.state.filters.some(filter => this.availableFilters && this.availableFilters[filter]?.run?.call(this, item))) {
				return false;
			}
			if (!this.search(item)) {
				return false;
			}
			return true;
		}).sort((a, b) => {
			const attrA = this.getItemAttribute(a, sort);
			const attrB = this.getItemAttribute(b, sort);
			// Always putting null/undefined last - should we add an option to change that?
			if (attrA == null) return attrB == null ? 0 : 1;
			if (attrB == null) return -1;
			if (typeof attrA === 'string' && typeof attrB === 'string') {
				return sortMult * attrA.localeCompare(attrB);
			}
			return sortMult * (attrA - attrB);
		});
	}

	selectView(e) {
		this.setState({ viewMode: e.target.value });
	}

	toggleColumn(e) {
		const columns = this.state.columns;
		columns[e.target.value] = e.target.checked;
		this.setState({ columns: columns });
	}

	clearFilters() {
		this.setState({ filters: [] });
	}

	selectFilter(e) {
		let filters = this.state.filters.slice();
		const { checked, value } = e.currentTarget;
		if (checked) {
			filters.push(value);
		} else {
			filters = filters.filter(f => f !== value);
		}
		// NOTE: could use view transitions for filtering actions
		this.setState({ filters });
	}

	onGridSortSelection(e) {
		const [sort, sortDirection] = e.target.value.split(':');
		this.setState({ sort, sortDirection });
	}

	onListSortSelection(e) {
		const key = e.currentTarget.value;
		const sortDirection = this.state.sort === key && this.state.sortDirection === 'asc' ? 'desc' : 'asc';
		this.setState({ sort: key, sortDirection });
	}

	onSearchInput(e) {
		this.setState({ search: e.currentTarget.value });
	}

	async showVersionHistory(item) {
		const path = getDataPathFromItem(item);
		await showModal(VersionHistory, { path });
	}

	preparePreview(item) {
		preparePreviewData(item);
	}

	getItemName(item) {
		return item.name;
	}
	getItemModifiedDate(item) {
		return item.commits?.current && new Date(item.commits.current.at);
	}
	getItemPublishedDate(item) {
		return item.commits?.publish && new Date(item.commits.publish.at);
	}
	getItemImage(item) {
		return item.src;
	}
	getItemLink(item) {
		return;
	}
	getItemStatus(item) {
		return item.commits?.publish ? 'live' : 'draft';
	}

	renderStatus(item) {
		const live = this.getItemStatus(item) === 'live';
		const hasChanges = live && item.commits?.current?.at > item.commits?.publish?.at;
		return this.renderBadge(live ? 'live' : 'draft', hasChanges);
	}

	renderSettingsButtons() {}
	renderSearch() {
		return (
			<input
				class={this.s.search}
				key={'search' + this.state.listId}
				type="text"
				placeholder="Search..."
				onInput={this.onSearchInput}
			/>
		);
	}

	renderViewModeOptions() {
		const optionalColumns = this.state.viewMode === 'list' && Object.entries(this.attributes).filter(([, details]) => !details.required);
		return [
			optionalColumns?.length && {
				title: 'Columns',
				content: optionalColumns.map(([attr, details]) => (
					<Button tag="label" class={this.s.toggle}>
						<input type="checkbox" value={attr} checked={this.state.columns[attr]} onChange={this.toggleColumn} />
						{details.name}
					</Button>
				)),
			},
		].filter(e => e);
	}

	renderSettings() {
		const state = this.state;
		const viewModeOptions = this.renderViewModeOptions();

		const filterOptions = this.getFilterOptions();
		const hasFilters = filterOptions.length > 1;
		const hasAvailableFilters = filterOptions.filter(option => !option.disabled).length > 1;

		return (
			<div class={this.s.settings}>
				{this.renderSettingsButtons()}
				{this.renderSearch()}
				<div class={this.s.filters}>
					<Dropdown class={this.s.viewMode} name="list-view" value={state.viewMode} label="View" icon={state.viewMode === 'grid' ? 'grid' : 'list'} noChevron onChange={this.selectView}>
						<div class={this.s.title}>View</div>
						<option value="grid" icon="grid">Grid</option>
						<option value="list" icon="list">List</option>
						{viewModeOptions.flatMap(group => [<hr />, !!group.title && <div class={this.s.title}>{group.title}</div>].concat(group.content)).filter(e => e)}
					</Dropdown>
					{hasFilters && (
						<Dropdown class={this.s.filter} name="list-filter" label="Filter" icon="filter" noChevron counter={this.state.filters.length || undefined} disabled={!hasAvailableFilters}>
							<div class={this.s.top}>
								<div class={this.s.title}>Filter</div>
								<Button class={this.s.clear} iconAlt="cancel" disabled={!this.state.filters.length} onClick={this.clearFilters}>Clear filters</Button>
							</div>
							<div class={this.s.options}>
								{filterOptions.map(({ key, name, disabled }) => (
									<Button outline tag="label">
										<input type="checkbox" value={key} checked={this.state.filters.includes(key)} disabled={disabled} onChange={this.selectFilter} />
										{name}
									</Button>
								))}
							</div>
						</Dropdown>
					)}
				</div>
			</div>
		);
	}

	renderItemMenu(item) {
		const key = item.id;
		const elements = this.renderItemMenuElements(item);
		if (!elements?.length) return;
		return (
			<Button class={this.s.menu} icon="dots-vertical" popovertarget={`grid-item-${key}-options`}>
				<div popover="auto" id={`grid-item-${key}-options`}>
					{elements}
				</div>
			</Button>
		);
	}

	renderItemMenuElements(item) {
		return [];
	}

	renderItemLink(item, content) {
		const link = this.getItemLink(item);
		if (!link) return content;
		return <a href={link}>{content}</a>;
	}

	renderGrid() {
		return (
			<div class={joinClasses(this.s.grid, this.s.content)}>
				{this.getItems().map(this.renderGridItem)}
			</div>
		);
	}

	renderGridSortOptions() {
		const allSortOptions = this.sortOptions.flatMap(key => ['desc', 'asc'].map(dir => ({ key, dir })));
		return (
			<div class={this.s.sortWrapper}>
				Sort by
				<Dropdown class={this.s.sortDropdown} name="list-grid-sort" value={`${this.state.sort}:${this.state.sortDirection}`} onChange={this.onGridSortSelection}>
					{allSortOptions.map(({ key, dir }) => {
						const attr = this.attributes[key];
						const sortNames = this.sortNames[attr?.type] || this.sortNames.default;
						return (
							<option value={`${key}:${dir}`}>
								{attr?.name || key}
								<span class={this.s.dir}>{sortNames[dir]}</span>
							</option>
						);
					})}
				</Dropdown>
			</div>
		);
	}

	renderGridPreview(item) {
		const src = this.getItemImage(item);
		return <div class={this.s.preview} style={{ backgroundImage: src ? `url('${src}')` : '' }} />;
	}

	renderGridItemName(item) {
		return this.renderItemLink(item, this.getItemName(item));
	}
	renderGridItemSubName(item) {}

	renderTimeInfo(time, def = 'Never', excludeTime = false) {
		if (!time || isNaN(time)) return <span>{def}</span>;
		return <time datetime={time.toISOString()}>{`${this.dateFormatter.format(time)}${excludeTime ? '' : ` ${this.timeFormatter.format(time)}`}`}</time>;
	}
	renderGridItemDetails(item) {
		return (
			<div class={this.s.details}>
				<div class={this.s.detail}>
					<div class={this.s.label}>Last modified</div>
					{this.renderTimeInfo(this.getItemModifiedDate(item))}
				</div>
				<div class={this.s.detail}>
					<div class={this.s.label}>Last published</div>
					{this.renderTimeInfo(this.getItemPublishedDate(item))}
				</div>
			</div>
		);
	}

	renderGridItem(item, i) {
		return (
			<div class={this.s.item} key={item.id || i}>
				{this.renderItemLink(item, this.renderGridPreview(item))}
				<div class={this.s.top}>
					<div class={this.s.name}>
						{this.renderGridItemName(item)}
					</div>
					{this.renderItemMenu(item)}
					{this.renderGridItemSubName(item)}
				</div>
				{this.renderGridItemDetails(item)}
			</div>
		);
	}

	renderListItemName(item) {
		return this.renderItemLink(item, this.getItemName(item));
	}

	renderListHeader(key) {
		const sortable = this.sortOptions.includes(key);
		// const sortable = true;
		const isSort = this.state.sort === key;
		const asc = this.state.sortDirection === 'asc';
		const content = this.attributes[key]?.name || key;
		return (
			<th>
				{!sortable ? <span class={this.s.raw}>{content}</span> : (
					<Button class={joinClasses(isSort && asc && this.s.sortAsc)} iconAlt={isSort ? 'chevron-down' : 'none'} onClick={this.onListSortSelection} value={key}>
						{content}
					</Button>
				)}
			</th>
		);
	}

	renderListCellContent(item, key) {
		const attribute = this.attributes[key];
		const value = this.getItemAttribute(item, key);
		if (attribute?.render) {
			return attribute.render.call(this, item, value);
		}
		if (attribute?.type === 'date' || attribute?.type === 'datetime') {
			return this.renderTimeInfo(value instanceof Date ? value : new Date(value), attribute?.default, attribute?.type === 'date');
		}
		return value;
	}

	renderList() {
		const attributes = this.attributes;
		let renderedAttributes = Object.keys(attributes).filter(key => !attributes[key]?.hideInList && this.state.columns[key]);
		const items = this.getItems();
		return (
			<div class={joinClasses(this.s.listWrapper, this.s.content)}>
				<table class={this.s.list}>
					<thead>
						<tr class={this.s.row}>
							{renderedAttributes.map(key => this.renderListHeader(key))}
							<th class={this.s.actions} />
						</tr>
					</thead>
					<tbody class={this.s.rows}>
						{items.map((item) => (
							<tr class={this.s.row}>
								{renderedAttributes.map(key => <td>{this.renderListCellContent(item, key)}</td>)}
								<td class={this.s.actions}>
									{this.renderItemMenu(item)}
								</td>
							</tr>
						))}
						{!items.length && (
							<tr class={joinClasses(this.s.row, this.s.empty)}>
								<td colspan={renderedAttributes.length + 1}>No results</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		);
	}

	renderBadge(type, changes) {
		const badge = <Badge type={type} alert={!!changes} />;
		if (!changes) {
			return badge;
		} else {
			return (
				<WithTooltip direction="bottom" align="start" tooltip="Live, with unpublished changes">
					{badge}
				</WithTooltip>
			);
		}
	}

	renderExtraHeaderContent() {
		return [
			this.renderSettings(),
			this.state.viewMode === 'grid' && this.renderGridSortOptions(),
		];
	}

	renderContent() {
		if (this.props.loading) return <LoadingBar class={this.s.loading} />;
		return this.viewModes[this.state.viewMode]?.render.call(this);
	}
}